import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Loader2, ChevronRight, ChevronLeft, Check, User, CreditCard, AlertCircle } from 'lucide-react';
import { PhoneInput } from '../ui/phone-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { clientsApi, comptesApi } from '../../api';
import type { AdminRegisterRequest, CreateCompteRequest, TypeCompte, Devise, FrequencePaiement } from '../../api/clients.api';
import { toast } from '../../hooks/use-toast';

import type { Client } from '../../api/clients.api';

interface CreateClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    existingClient?: Client | null;
}

interface Step1Data {
    prenom: string;
    nom: string;
    surnom: string;
    telephone: string;
    email: string;
    sexe: string;
    adresse: string;
    ville: string;
    dateNaissance: string;
}

interface Step2Data {
    type: TypeCompte;
    devise: Devise;
    planSol: string;
    dateDebut: string;
    frequence: FrequencePaiement;
    duree: number;
    nbMain: number;
    sol: number;
}

interface FieldErrors {
    prenom?: string;
    nom?: string;
    telephone?: string;
    email?: string;
    general?: string;
}

const initialStep1: Step1Data = {
    prenom: '',
    nom: '',
    surnom: '',
    telephone: '',
    email: '',
    sexe: '',
    adresse: '',
    ville: '',
    dateNaissance: '',
};

// Retounen dat jounen an nan timezone lokal (evite bug UTC)
const getLocalToday = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Kalkile dat fin SOL selon lojik backend: jours sote dimanch
const calculerDateFin = (dateDebut: string, duree: number, frequence: string): string => {
    if (!dateDebut || !duree || duree <= 0) return '';
    // Itilize midi pou evite bug changement d'heure
    const current = new Date(dateDebut + 'T12:00:00');
    if (isNaN(current.getTime())) return '';

    if (frequence === 'jours') {
        // Konte duree jou ki pa dimanch (0 = Dimanche)
        let count = 0;
        while (count < duree) {
            if (current.getDay() !== 0) count++;
            if (count < duree) current.setDate(current.getDate() + 1);
        }
    } else if (frequence === 'semaine') {
        current.setDate(current.getDate() + (duree - 1) * 7);
    } else if (frequence === 'mois') {
        current.setMonth(current.getMonth() + (duree - 1));
    }

    return `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
};

const initialStep2: Step2Data = {
    type: 'BS',
    devise: 'HTG',
    planSol: '',
    dateDebut: getLocalToday(),
    frequence: 'jours',
    duree: 30,
    nbMain: 1,
    sol: 100,
};

export function CreateClientModal({ isOpen, onClose, onSuccess, existingClient }: CreateClientModalProps) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    // Alert Dialog State
    const [showAlert, setShowAlert] = useState(false);
    const [pendingAction, setPendingAction] = useState<'close' | 'back' | null>(null);

    // Input refs for focus
    const prenomRef = useRef<HTMLInputElement>(null);
    const nomRef = useRef<HTMLInputElement>(null);
    const telephoneRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);

    // Step 1 data
    const [step1Data, setStep1Data] = useState<Step1Data>(initialStep1);

    // Step 2 data
    const [step2Data, setStep2Data] = useState<Step2Data>(initialStep2);

    // Data from step 1 response
    const [clientId, setClientId] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_pinSetupUrl, setPinSetupUrl] = useState<string | null>(null);

    // If existingClient is provided, skip to step 2
    useEffect(() => {
        if (existingClient && isOpen) {
            setClientId(existingClient.id);
            setStep(2);
        }
    }, [existingClient, isOpen]);

    // Clear field error when input changes
    const handleStep1Change = (field: keyof Step1Data, value: string) => {
        setStep1Data({ ...step1Data, [field]: value });
        // Clear error for this field and general error
        if (fieldErrors[field as keyof FieldErrors] || fieldErrors.general) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field as keyof FieldErrors];
                delete newErrors.general;
                return newErrors;
            });
        }
    };

    const resetForm = () => {
        setStep(1);
        setStep1Data(initialStep1);
        setStep2Data(initialStep2);
        setClientId(null);
        setPinSetupUrl(null);
        setFieldErrors({});
    };

    const handleClose = () => {
        if (step === 2 && clientId) {
            setPendingAction('close');
            setShowAlert(true);
        } else {
            resetForm();
            onClose();
        }
    };

    const confirmClose = () => {
        // ONLY rollback if we created this client in this session AND it wasn't an existing client
        if (clientId && !existingClient) {
            clientsApi.rollback(clientId).catch(console.error);
        }
        resetForm();
        onClose();
        setShowAlert(false);
        setPendingAction(null);
    };

    const validateStep1 = (): boolean => {
        const errors: FieldErrors = {};

        if (!step1Data.prenom.trim()) {
            errors.prenom = t('common:validation.required');
        }
        if (!step1Data.nom.trim()) {
            errors.nom = t('common:validation.required');
        }
        if (!step1Data.telephone.trim()) {
            errors.telephone = t('common:validation.required');
        } else if (!isValidPhoneNumber(step1Data.telephone)) {
            errors.telephone = t('common:validation.invalidPhone');
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            // Focus first field with error
            if (errors.prenom) prenomRef.current?.focus();
            else if (errors.nom) nomRef.current?.focus();
            else if (errors.telephone) telephoneRef.current?.focus();
            return false;
        }

        setFieldErrors({});
        return true;
    };

    const handleStep1Submit = async () => {
        if (!validateStep1()) return;

        setIsLoading(true);
        setFieldErrors({});

        try {
            const request: AdminRegisterRequest = {
                prenom: step1Data.prenom.trim(),
                nom: step1Data.nom.trim(),
                surnom: step1Data.surnom.trim() || undefined,
                telephone: step1Data.telephone,
                email: step1Data.email.trim() || undefined,
                sexe: step1Data.sexe || undefined,
                adresse: step1Data.adresse.trim() || undefined,
                ville: step1Data.ville.trim() || undefined,
                dateNaissance: step1Data.dateNaissance || undefined,
                frontendUrl: window.location.origin,
            };

            const response = await clientsApi.adminRegister(request);

            if (response.success) {
                setClientId(response.data.clientId);
                setPinSetupUrl(response.data.pinSetupUrl);
                setStep(2);
                toast({
                    title: t('accounts:clients.dialogs.create.step1Complete'),
                    description: t('accounts:clients.dialogs.create.step1Description'),
                    type: 'success',
                });
            }
        } catch (err: any) {
            const message = err.response?.data?.message || t('accounts:clients.dialogs.create.errorStep1');

            // Detect which field has error based on message
            const msgLower = message.toLowerCase();
            if (msgLower.includes('email') || msgLower.includes('imèl')) {
                setFieldErrors({ email: message });
                emailRef.current?.focus();
            } else if (msgLower.includes('telefòn') || msgLower.includes('telephone') || msgLower.includes('nimewo')) {
                setFieldErrors({ telephone: message });
                telephoneRef.current?.focus();
            } else {
                setFieldErrors({ general: message });
            }
            console.error('Step 1 error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStep2Submit = async () => {
        if (!clientId) {
            setFieldErrors({ general: t('common:errors.unknown') });
            return;
        }

        setIsLoading(true);
        setFieldErrors({});

        try {
            const request: CreateCompteRequest = {
                type: step2Data.type,
                clientId: clientId,
                devise: step2Data.devise,
            };

            if (step2Data.type === 'SOL') {
                request.dateDebut = step2Data.dateDebut;
                request.frequence = step2Data.frequence;
                request.duree = step2Data.duree;
                request.nbMain = step2Data.nbMain;
                request.sol = step2Data.sol;
            }

            if (step2Data.type === 'BS' && step2Data.planSol) {
                request.planSol = step2Data.planSol;
            }

            const response = await comptesApi.createForAdmin(request);

            if (response.success) {
                toast({
                    title: t('accounts:clients.dialogs.create.success'),
                    description: t('accounts:clients.dialogs.create.accountCode', { code: response.compte.code }),
                    type: 'success',
                });
                resetForm();
                onSuccess();
                onClose();
            }
        } catch (err: any) {
            const message = err.response?.data?.message || t('accounts:clients.dialogs.create.errorStep2');
            setFieldErrors({ general: message });
            console.error('Step 2 error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        // If adding account to existing client, just close the modal
        if (existingClient) {
            handleClose();
            return;
        }

        if (step === 2 && clientId) {
           setPendingAction('back');
           setShowAlert(true);
        }
    };

    const confirmBack = () => {
        if (clientId) {
             clientsApi.rollback(clientId).catch(console.error);
             setClientId(null);
             setPinSetupUrl(null);
             setStep(1);
        }
        setShowAlert(false);
        setPendingAction(null);
    };

    const handleAlertConfirm = () => {
        if (pendingAction === 'close') {
            confirmClose();
        } else if (pendingAction === 'back') {
            confirmBack();
        }
    };

    // Helper for input styling
    const getInputClassName = (field: keyof FieldErrors) => {
        return `h-10 ${fieldErrors[field] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`;
    };

    return (
        <>
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader className="text-center pb-2">
                    <DialogTitle className="flex items-center justify-center gap-2 text-lg">
                        {existingClient ? (
                            <>
                                <div className="bg-green-100 p-2 rounded-full">
                                    <CreditCard className="h-5 w-5 text-green-600" />
                                </div>
                                {t('accounts:clients.dialogs.create.newAccountFor', { name: `${existingClient.prenom} ${existingClient.nom}` })}
                            </>
                        ) : step === 1 ? (
                            <>
                                <div className="bg-primary-100 p-2 rounded-full">
                                    <User className="h-5 w-5 text-primary-600" />
                                </div>
                                {t('accounts:clients.dialogs.create.clientInfo')}
                            </>
                        ) : (
                            <>
                                <div className="bg-primary-100 p-2 rounded-full">
                                    <CreditCard className="h-5 w-5 text-primary-600" />
                                </div>
                                {t('accounts:clients.dialogs.create.configAccount')}
                            </>
                        )}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        {step === 1
                            ? t('accounts:clients.dialogs.create.descriptionStep1')
                            : t('accounts:clients.dialogs.create.descriptionStep2')}
                    </DialogDescription>
                </DialogHeader>

                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-3 py-3">
                    <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                            {step > 1 ? <Check className="h-4 w-4" /> : '1'}
                        </div>
                        <span className="text-xs mt-1 text-gray-500">{t('common:labels.info')}</span>
                    </div>
                    <div className={`w-12 sm:w-20 h-1 rounded ${step > 1 ? 'bg-primary-600' : 'bg-gray-200'}`} />
                    <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                            2
                        </div>
                        <span className="text-xs mt-1 text-gray-500">{t('common:labels.account')}</span>
                    </div>
                </div>

                {/* General error message */}
                {fieldErrors.general && (
                    <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{fieldErrors.general}</span>
                    </div>
                )}

                {/* Step 1: Client Info */}
                {step === 1 && (
                    <div className="space-y-4">
                        {/* Prenom & Nom */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="prenom" className="text-sm font-medium">
                                    {t('common:labels.firstName')} <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    ref={prenomRef}
                                    id="prenom"
                                    placeholder="Jean"
                                    value={step1Data.prenom}
                                    onChange={(e) => handleStep1Change('prenom', e.target.value)}
                                    disabled={isLoading}
                                    className={getInputClassName('prenom')}
                                />
                                {fieldErrors.prenom && (
                                    <p className="text-xs text-red-500">{fieldErrors.prenom}</p>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="nom" className="text-sm font-medium">
                                    {t('common:labels.lastName')} <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    ref={nomRef}
                                    id="nom"
                                    placeholder="Dupont"
                                    value={step1Data.nom}
                                    onChange={(e) => handleStep1Change('nom', e.target.value)}
                                    disabled={isLoading}
                                    className={getInputClassName('nom')}
                                />
                                {fieldErrors.nom && (
                                    <p className="text-xs text-red-500">{fieldErrors.nom}</p>
                                )}
                            </div>
                        </div>

                        {/* Surnom */}
                        <div className="space-y-1.5">
                            <Label htmlFor="surnom" className="text-sm font-medium">{t('common:labels.nickname')}</Label>
                            <Input
                                id="surnom"
                                placeholder={t('accounts:clients.dialogs.create.nicknamePlaceholder')}
                                value={step1Data.surnom}
                                onChange={(e) => handleStep1Change('surnom', e.target.value)}
                                disabled={isLoading}
                                className="h-10"
                            />
                        </div>

                        {/* Telefòn */}
                        <div className="space-y-1.5">
                            <Label htmlFor="telephone" className="text-sm font-medium">
                                {t('common:labels.phone')} <span className="text-red-500">*</span>
                            </Label>
                            <div ref={telephoneRef as any}>
                                <PhoneInput
                                    id="telephone"
                                    placeholder={t('accounts:clients.dialogs.create.phonePlaceholder')}
                                    value={step1Data.telephone}
                                    onChange={(value: string | undefined) => handleStep1Change('telephone', value || '')}
                                    defaultCountry="HT"
                                    disabled={isLoading}
                                    className={fieldErrors.telephone ? 'border-red-500' : ''}
                                />
                            </div>
                            {fieldErrors.telephone && (
                                <p className="text-xs text-red-500">{fieldErrors.telephone}</p>
                            )}
                        </div>

                        {/* Email */}
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-sm font-medium">{t('common:labels.email')}</Label>
                            <Input
                                ref={emailRef}
                                id="email"
                                type="email"
                                placeholder="jean@example.com"
                                value={step1Data.email}
                                onChange={(e) => handleStep1Change('email', e.target.value)}
                                disabled={isLoading}
                                className={getInputClassName('email')}
                            />
                            {fieldErrors.email && (
                                <p className="text-xs text-red-500">{fieldErrors.email}</p>
                            )}
                        </div>

                        {/* Sèks & Dat Nesans */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="sexe" className="text-sm font-medium">{t('common:labels.gender')}</Label>
                                <Select
                                    value={step1Data.sexe}
                                    onValueChange={(value) => handleStep1Change('sexe', value)}
                                    disabled={isLoading}
                                >
                                    <SelectTrigger className="h-10">
                                        <SelectValue placeholder={t('common:labels.choose')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="M">{t('common:labels.male')}</SelectItem>
                                        <SelectItem value="F">{t('common:labels.female')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="dateNaissance" className="text-sm font-medium">{t('common:labels.birthDate')}</Label>
                                <Input
                                    id="dateNaissance"
                                    type="date"
                                    value={step1Data.dateNaissance}
                                    onChange={(e) => handleStep1Change('dateNaissance', e.target.value)}
                                    disabled={isLoading}
                                    className="h-10"
                                />
                            </div>
                        </div>

                        {/* Adrès & Vil */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="adresse" className="text-sm font-medium">{t('common:labels.address')}</Label>
                                <Input
                                    id="adresse"
                                    placeholder="123 Rue..."
                                    value={step1Data.adresse}
                                    onChange={(e) => handleStep1Change('adresse', e.target.value)}
                                    disabled={isLoading}
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="ville" className="text-sm font-medium">{t('common:labels.city')}</Label>
                                <Input
                                    id="ville"
                                    placeholder="Port-au-Prince"
                                    value={step1Data.ville}
                                    onChange={(e) => handleStep1Change('ville', e.target.value)}
                                    disabled={isLoading}
                                    className="h-10"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Account Config */}
                {step === 2 && (
                    <div className="space-y-4">
                        {/* Tip Kont & Deviz */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium">
                                    {t('common:labels.type')} <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                    value={step2Data.type}
                                    onValueChange={(value: TypeCompte) => setStep2Data({ ...step2Data, type: value, planSol: 'none' })}
                                    disabled={isLoading}
                                >
                                    <SelectTrigger className="h-10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="BS">BS (Bousanm Standard)</SelectItem>
                                        <SelectItem value="SOL">SOL (Sabotay)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium">
                                    {t('common:labels.currency')} <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                    value={step2Data.devise}
                                    onValueChange={(value: Devise) => setStep2Data({ ...step2Data, devise: value })}
                                    disabled={isLoading}
                                >
                                    <SelectTrigger className="h-10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="HTG">HTG (Goud)</SelectItem>
                                        <SelectItem value="USD">USD (Dola)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* SOL specific fields */}
                        {step2Data.type === 'SOL' && (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-medium">{t('accounts:clients.dialogs.create.startDate')}</Label>
                                        <Input
                                            type="date"
                                            value={step2Data.dateDebut}
                                            min={getLocalToday()}
                                            onChange={(e) => setStep2Data({ ...step2Data, dateDebut: e.target.value })}
                                            disabled={isLoading}
                                            className="h-10"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-medium">{t('accounts:clients.dialogs.create.frequency')}</Label>
                                        <Select
                                            value={step2Data.frequence}
                                            onValueChange={(value: FrequencePaiement) => setStep2Data({ ...step2Data, frequence: value })}
                                            disabled={isLoading}
                                        >
                                            <SelectTrigger className="h-10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="jours">{t('accounts:clients.dialogs.create.daily')}</SelectItem>
                                                <SelectItem value="semaine">{t('accounts:clients.dialogs.create.weekly')}</SelectItem>
                                                <SelectItem value="mois">{t('accounts:clients.dialogs.create.monthly')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-medium">{t('accounts:clients.dialogs.create.duration')}</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={step2Data.duree}
                                            onChange={(e) => setStep2Data({ ...step2Data, duree: parseInt(e.target.value) || 0 })}
                                            disabled={isLoading}
                                            className="h-10"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-medium">{t('accounts:clients.dialogs.create.hands')}</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={step2Data.nbMain}
                                            onChange={(e) => setStep2Data({ ...step2Data, nbMain: parseInt(e.target.value) || 0 })}
                                            disabled={isLoading}
                                            className="h-10"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-medium">{t('accounts:clients.dialogs.create.sol')}</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={step2Data.sol}
                                            onChange={(e) => setStep2Data({ ...step2Data, sol: parseInt(e.target.value) || 0 })}
                                            disabled={isLoading}
                                            className="h-10"
                                        />
                                    </div>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800 space-y-1">
                                    <div className="text-center">
                                        <strong>{t('accounts:clients.dialogs.create.amountPerDay')}</strong>{' '}
                                        {step2Data.nbMain * step2Data.sol} {step2Data.devise}
                                    </div>
                                    {(() => {
                                        const dateFin = calculerDateFin(step2Data.dateDebut, step2Data.duree, step2Data.frequence);
                                        if (!dateFin) return null;
                                        const d = new Date(dateFin + 'T12:00:00');
                                        const label = d.toLocaleDateString('fr-HT', { day: '2-digit', month: 'long', year: 'numeric' });
                                        return (
                                            <div className="text-center text-blue-600">
                                                <strong>Dat fin:</strong> {label}
                                                {/* {step2Data.frequence === 'jours' && (
                                                    
                                                )} */}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </>
                        )}

                        {/* PIN setup URL info */}
                        {_pinSetupUrl && (
                            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
                                <strong>📧 {t('accounts:clients.dialogs.create.pinLink')}</strong> {t('accounts:clients.dialogs.create.pinDescription')}
                            </div>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t">
                    {step === 1 ? (
                        <>
                            <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
                                {t('common:buttons.cancel')}
                            </Button>
                            <Button onClick={handleStep1Submit} disabled={isLoading} className="min-w-[120px]">
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('common:loading')}
                                    </>
                                ) : (
                                    <>
                                        {t('common:buttons.next')}
                                        <ChevronRight className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={handleBack} disabled={isLoading}>
                                <ChevronLeft className="mr-2 h-4 w-4" />
                                {t('common:buttons.back')}
                            </Button>
                            <Button onClick={handleStep2Submit} disabled={isLoading} className="min-w-[120px]">
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('common:loading')}
                                    </>
                                ) : (
                                    <>
                                        <Check className="mr-2 h-4 w-4" />
                                        {t('accounts:clients.dialogs.create.createAccountButton')}
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>

        </Dialog>

        <AlertDialog open={showAlert} onOpenChange={setShowAlert}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('common:alerts.warning') || 'Attention'}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {existingClient ? (
                            'Annuler la création du compte ? Le client ne sera pas supprimé.'
                        ) : (
                            pendingAction === 'close' 
                                ? t('accounts:clients.dialogs.create.confirmClose')
                                : t('accounts:clients.dialogs.create.confirmBack')
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => { setShowAlert(false); setPendingAction(null); }}>
                        {t('common:buttons.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={handleAlertConfirm} className="bg-red-600 hover:bg-red-700">
                        {t('common:buttons.confirm')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        </>
    );
}

export default CreateClientModal;
